import React from 'react'
import { Accordion, Alert, Button, Col, Container, Row } from 'react-bootstrap'
import CloseIcon from '../../images/icons/close_panel.svg'
import CSVReader from 'react-csv-reader'
import { ImportCost } from '../../../../back-end/common/costs'
import * as yup from 'yup'
import { yupToFormErrors } from 'formik'
import { Loading } from '../ui/Loading/Loading'
import { DateTime } from 'luxon'

interface UploadModalProps {
	onClose: () => void
	handleBulkCostInsert: (costs: ImportCost[]) => void
}

const parseOptions = {
	header: true,
	dynamicTyping: true,
	skipEmptyLines: true,
	transformHeader: (header: string) => header.toLowerCase().replace(/\s/g, ''), // make everything lowercase
}

const isEmptyRow = (row: { [s: string]: unknown }) => {
	return Object.values(row).every((value) => value === null || value === undefined || value === '')
}

const costSchema = yup.array().of(
	yup.object().shape({
		jobnumber: yup.string().required().typeError('Job Number must not be empty'),
		description: yup.string().required().typeError('Description must not be empty'),
		price: yup.number().required().typeError('Price must not be empty and include only numbers'),
		date: yup
			.string()
			.nullable()
			.test('is-valid-date', 'Date must be a valid date in YYYY-MM-DD, d/M/yyyy or yyyy/M/d format', (value) => {
				if (!value) {
					return true
				}
				return DateTime.fromISO(value).isValid || DateTime.fromFormat(value, 'd/M/yyyy').isValid || DateTime.fromFormat(value, 'yyyy/M/d').isValid
			}),
		code: yup.string().nullable(),
		cost: yup.number().nullable().typeError('Cost must include only numbers'),
		supplier: yup.string().nullable(),
		note: yup.string().nullable(),
		billable: yup.string().nullable(),
		quantity: yup.number().required().typeError('Quantity must include only numbers'),
	})
)

type FieldErrors = Record<string, string | undefined>
type RowErrors = Record<number, FieldErrors>

const formatErrorsDisplay = (rowErrors: RowErrors) => {
	const grouped: Record<
		string,
		{
			field: string
			message: string
			count: number
			rows: number[]
		}
	> = {}

	for (const [rowStr, fields] of Object.entries(rowErrors)) {
		const row = parseInt(rowStr, 10)
		for (const [field, message] of Object.entries(fields)) {
			const key = message ?? 'Unknown error'
			if (!grouped[key]) {
				grouped[key] = {
					field,
					message: key,
					count: 0,
					rows: [],
				}
			}
			grouped[key].count++
			grouped[key].rows.push(Number(row + 2))
		}
	}

	return Object.values(grouped).sort((a, b) => b.count - a.count)
}

const UploadModal = (props: UploadModalProps) => {
	const [uploadedCosts, setUploadedCosts] = React.useState<ImportCost[] | null>(null)
	const [issues, setIssues] = React.useState<ReturnType<typeof formatErrorsDisplay> | null>(null)

	const [submitted, setSubmitted] = React.useState<boolean>(false)

	const handleFileLoaded = React.useCallback(async (data: unknown[]) => {
		setIssues(null)
		try {
			const cleanedData = data.filter((row) => !isEmptyRow(row as { [s: string]: unknown }))
			await costSchema.validate(cleanedData, { abortEarly: false })
			setUploadedCosts(cleanedData as ImportCost[])
		} catch (err) {
			const errors = yupToFormErrors<unknown[]>(err)

			// if there are as many errors as lines it's an invalid file
			if (Object.keys(errors).length === data.length) {
				const firstEntry: any = errors['1'] // eslint-disable-line
				const individualErrors = Object.keys(firstEntry)
				setIssues([
					{
						field: 'file',
						message:
							'Invalid file format, missing required columns: ' + individualErrors.map((error) => error.replace(/\[\d+\]\./g, '')).join(', '),
						count: data.length,
						rows: Array.from({ length: data.length }, (_, i) => i + 2),
					},
				])
			} else {
				const grouped = formatErrorsDisplay(errors as unknown as RowErrors)
				setIssues(grouped)
			}
		}
	}, [])

	const handleUploadClick = React.useCallback(
		(uploadedCosts: ImportCost[]) => {
			props.handleBulkCostInsert(uploadedCosts || [])
			setSubmitted(true)
		},
		[setSubmitted, props.handleBulkCostInsert]
	)

	return (
		<div className={'estimate-modal'}>
			<Container>
				<Row className="pb-3">
					<Col onClick={() => props.onClose()} className="panel-close-btn" sm="auto">
						<img src={CloseIcon} alt="Close" className="pr-2" />
						<span className="span-bold span-navy span-body-2">Close</span>
					</Col>
				</Row>
				<Row className="pb-3">
					<Col>
						<CSVReader onFileLoaded={handleFileLoaded} parserOptions={parseOptions} cssClass="csv-reader" />
					</Col>
				</Row>
				{issues && issues.length > 0 && (
					<Row>
						<Col>
							<Alert variant="danger">
								{issues[0].field === 'file' ? (
									issues[0].message
								) : (
									<>
										<p>The following issues are present:</p>
										<Accordion defaultActiveKey={null}>
											{issues.map((issue, index) => (
												<Accordion.Item eventKey={index.toString()} key={index}>
													<Accordion.Header>
														{issue.message}&nbsp;–&nbsp;<strong>{issue.rows.length}</strong>&nbsp; row
														{issue.rows.length > 1 ? 's' : ''}
													</Accordion.Header>
													<Accordion.Body>
														<p>
															<strong>Rows:</strong> {issue.rows.join(', ')}
														</p>
													</Accordion.Body>
												</Accordion.Item>
											))}
										</Accordion>
									</>
								)}
							</Alert>
						</Col>
					</Row>
				)}

				<Row>
					<Col>
						<Button onClick={() => handleUploadClick(uploadedCosts || [])} disabled={issues !== null || uploadedCosts === null || submitted}>
							Upload{uploadedCosts ? ` ${uploadedCosts.length} Costs` : ''}
							{submitted ? <Loading show size="sm" style={{ marginLeft: '10px' }} /> : ''}
						</Button>
					</Col>
				</Row>
			</Container>
		</div>
	)
}

export default UploadModal
